/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Modal,
  Spinner,
  SvgIcon,
  LeftArrow,
  LoanApplicationPropertiesBar,
  CreateCaseModal,
  LoanApplicationInfo as Info,
  LoanApplicationIncome as Income,
  LoanApplicationSecurity as Security,
  LoanApplicationExpenses as Expenses,
  LoanApplicationLoans as Loans,
  LoanApplicationCreditReport as CreditReport,
  LoanApplicationRetail as Retail,
  LoanApplicationCalculations as Calculations,
  LoanApplicationRequestedLoans as RequestedLoans,
  LoanApplicationReview as Review,
  LoanApplicationFinalise as Finalise,
  LoanApplicationIssue as Issue,
  LoanApplicationDisburse as DisburseAndActivate,
  RelatedFiles as FileViewer,
  Warning,
  ExpandableOverviewItem,
  LoanApplicationFileTypeFilter
} from '../'

import {
  decodeBase64,
  encodeBase64
} from '../../utils'

import {
  QUERY_CASERELATIONS
} from '../../queries'

import {
  CONTENTTYPE_FILE,
  CONTENTTYPE_REQUESTRUN,
  CONTENTTYPE_EMAIL,
  CONTENTTYPE_LOANAPPLICATION
} from '../../constants'

const QUERY_CREDITREPORTACCOUNTS = gql`
  query($creditReport: UUID!) {
    creditreportaccounts(creditreport_Id: $creditReport) {
      edges {
        node {
          id
          balance
          instalment
          paymentHistory
          creditreportsupplier {
            id
            name
          }
          consolidation {
            edges {
              node {
                id
                amount
              }
            }
          }
        }
      }
    }
  }
`

export default class LoanApplicationModal extends React.Component {
  static propTypes = {
    loanApplication: PropTypes.object.isRequired,
    refetchData: PropTypes.func.isRequired,
    updateLoanApplication: PropTypes.func.isRequired,
    updateLoan: PropTypes.func.isRequired,
    markFileAsTrash: PropTypes.func,
    onClose: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)

    this.state = {
      infoOpen: false,
      payslipsOpen: false,
      expensesOpen: false,
      loansOpen: false,
      creditReportOpen: false,
      retailOpen: false,
      securityOpen: false,
      calculationsOpen: false,
      requestedLoansOpen: false,
      approveOpen: false,
      finaliseOpen: false,
      issueOpen: false,
      disburseOpen: false,
      showCreateCaseModal: false,
      assignedTo: null,
      selectedFile: null,
      fileViewerFile: null,
      fileViewerFileTypeShortCode: null
    }

    this.loanApplicationPropertiesBarRef = React.createRef()
    this.loanApplicationModalRef = React.createRef()
    this.createCaseModalRef = React.createRef()
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (e) => {
    if (this.loanApplicationModalRef.current && !this.loanApplicationModalRef.current.contains(e.target) && !this.createCaseModalRef.current) {
      // click is outside, close drop-down(s)
      this.props.onClose()
    }

    if (this.createCaseModalRef.current && !this.createCaseModalRef.current.contains(e.target)) {
      // click is outside, close drop-down(s)
      this.closeCreateCaseModal()
    }
  }

  getCaseRelations = (file, requestRun) => {
    let relations = []
    if (requestRun) {
      if (requestRun.sources) {
        requestRun.sources.forEach(source => {
          relations.push({ objectId: decodeBase64(source.id), contentType: CONTENTTYPE_FILE })
          this.addEmailRelation(source, relations)
        })
      }
      relations.push({ objectId: decodeBase64(requestRun.id), contentType: CONTENTTYPE_REQUESTRUN })
    } else {
      relations.push({ objectId: decodeBase64(file.id), contentType: CONTENTTYPE_FILE })
      this.addEmailRelation(file, relations)
    }

    return relations
  }

  addEmailRelation = (file, relations) => {
    if (file.relations.edges.length > 0) {
      let emailRelation = file.relations.edges.find(relation => decodeBase64(relation.node.contentType.id) === CONTENTTYPE_EMAIL.toString())
      // if file has a filerelation to an email, add caserelation to email
      if (emailRelation) {
        relations.push({ objectId: encodeBase64(emailRelation.node.objectId, 'EmailNode'), contentType: CONTENTTYPE_EMAIL })
      }
    }
  }

  saveAssignedTo = (identity) => {
    this.props.updateLoanApplication({ assignedTo: identity })
  }

  handleSelectedFileChange = (file) => this.setState({ fileViewerFile: file, selectedFile: file, fileViewerFileTypeShortCode: null })

  closeCreateCaseModal = () => this.setState({ showCreateCaseModal: false })

  changeFileType = (fileTypeShortCode) => this.setState({ fileViewerFileTypeShortCode: fileTypeShortCode, selectedFile: null, fileViewerFile: null })

  render() {
    let loanApplication = this.props.loanApplication
    if (loanApplication) {
      let client = loanApplication.client
      let loans = loanApplication.loans.edges.map(loan => loan.node)
      let loansWithLmsIds = loans.filter(loan => loan.lmsId !== null)
      let loansWorkflowRunsProcessing = loansWithLmsIds.filter(loan => loan.workflowrunProcessing !== null)

      return (
        <Modal
          ref={this.loanApplicationModalRef}
          body={this.state.showCreateCaseModal
            ? <CreateCaseModal
                forwardedRef={this.createCaseModalRef}
                productType='loans'
                requestType='Application'
                followUp={true}
                affects={client ? client.identity.id : null}
                caseRelations={[ { objectId: decodeBase64(loanApplication.id),  contentType: CONTENTTYPE_LOANAPPLICATION }]}
                onCloseClick={this.closeCreateCaseModal}
                onSaveClick={this.closeCreateCaseModal}
                onCaseRelationsCreated={this.loanApplicationPropertiesBarRef.current.props.updateData}
              />
            : <div className='loan-application-modal slds-size_1-of-1'>
                <div className='loan-application-modal-nav slds-border_bottom page-navigation'>
                  <div className='u-font-size-larger clickable' onClick={this.props.onClose}>
                    <LeftArrow />
                    Back
                  </div>
                </div>
                <div className='slds-scrollable_y loan-application-modal-file-view'>
                  <LoanApplicationFileTypeFilter
                    value={this.state.fileViewerFileTypeShortCode}
                    onChange={this.changeFileType}
                    allOnly={this.state.fileViewerFile}
                  />
                  <FileViewer
                    objectId={decodeBase64(loanApplication.id)}
                    fileUploaded={this.state.fileUploaded}
                    preview={true}
                    file={this.state.fileViewerFile}
                    fileTypeShortCode={this.state.fileViewerFileTypeShortCode}
                    selectFile={this.state.selectedFile}
                    displayChildren={false}
                    onClick={file => this.setState({ selectedFile: file })}
                  />
                </div>
                <div className='slds-scrollable_y loan-application-modal-detail slds-size_1-of-1'>
                  <React.Fragment>
                    <Query query={QUERY_CASERELATIONS} variables={{ objectId: decodeBase64(loanApplication.id) }}>
                      {({ loading, error, data: crData, refetch }) => {
                        if (loading) {
                          return <Spinner className='slds-is-relative slds-m-left_large' size='x-small' />
                        }
                        if (error) {
                          return (
                            <Warning iconSize='x-small' inline={true} className='slds-m-left_small slds-m-right_small'>
                              Failed to load case relation information.
                            </Warning>
                          )
                        }

                        let openCases = crData.caserelations.edges.filter(caseRelation => (
                          caseRelation.node.case.status === 'OPEN'
                        ))
                        let resolvedCases = crData.caserelations.edges.filter(caseRelation => (
                          caseRelation.node.case.status === 'RESOLVED'
                        ))

                        return (
                          <LoanApplicationPropertiesBar
                            ref={this.loanApplicationPropertiesBarRef}
                            loanApplication={loanApplication.id}
                            loanApplicationStatus={loanApplication.status}
                            assignedToIdentity={loanApplication.assignedTo}
                            client={client}
                            openCases={openCases}
                            resolvedCases={resolvedCases}
                            onCreateCaseClick={() => this.setState({ showCreateCaseModal: true })}
                            assignedToSave={this.saveAssignedTo}
                            updateData={refetch}
                          />
                        )
                      }}
                    </Query>
                    <div className='slds-m-top_large slds-m-bottom_large slds-m-right_large'>
                      <Info
                        identity={client.identity}
                        loanApplicationStatus={loanApplication.status}
                        employmentType={loanApplication.employmentType}
                        preferredCallTime={loanApplication.calltime}
                        preferredContactNumber={loanApplication.addressValue}
                        bankName={loanApplication.bankaccount ? loanApplication.bankaccount.bankName : null}
                        bankAccountNumber={loanApplication.bankaccount ? loanApplication.bankaccount.accountNumber: null}
                        cashAppliedFor={loanApplication.cashAppliedFor}
                        retailAppliedFor={loanApplication.retailAppliedFor}
                        consolidationAppliedFor={loanApplication.consolidationAppliedFor}
                        open={this.state.infoOpen}
                        updateLoanApplication={this.props.updateLoanApplication}
                        onClick={() => this.setState(prevState => ({ infoOpen: !prevState.infoOpen }))}
                        onComplete={() => this.setState({ infoOpen: false, payslipsOpen: true })}
                      />
                      <Income
                        client={client}
                        employmentCommencedAt={loanApplication.employmentCommencedAt}
                        payslips={loanApplication.payslips.edges.map(payslip => payslip.node)}
                        open={this.state.payslipsOpen}
                        onClick={() => this.setState(prevState => ({ payslipsOpen: !prevState.payslipsOpen }))}
                        onComplete={() => this.setState({ payslipsOpen: false, expensesOpen: true })}
                      />
                      <Security
                        clientContribution={loanApplication.clientContribution}
                        clientIsInvestingOnePercent={JSON.parse(loanApplication.stages)['2_Payslips']['Client is LT investing 1% of salary']}
                        open={this.state.securityOpen}
                        onClick={() => this.setState(prevState => ({ securityOpen: !prevState.securityOpen }))}
                        onComplete={() => this.setState({ securityOpen: false, calculationsOpen: true })}
                      />
                      <Expenses
                        expenses={loanApplication.expenses.edges.map(expense => expense.node)}
                        open={this.state.expensesOpen}
                        onClick={() => this.setState(prevState => ({ expensesOpen: !prevState.expensesOpen }))}
                        onComplete={() => this.setState({ expensesOpen: false, loansOpen: true, creditReportOpen: true })}
                      />
                      <Loans
                        client={client.id}
                        clientLoans={client.loans.edges}
                        open={this.state.loansOpen}
                        onClick={() => this.setState(prevState => ({ loansOpen: !prevState.loansOpen }))}
                        onComplete={() => this.setState({ loansOpen: false, creditReportOpen: true })}
                      />
                      {loanApplication.creditreport
                        ? <Query
                            query={QUERY_CREDITREPORTACCOUNTS}
                            variables={{
                              creditReport: decodeBase64(loanApplication.creditreport.id)
                            }}>
                            {({ loading, error, data }) => {
                              if (loading) {
                                return (
                                  <ExpandableOverviewItem
                                    title='Credit report'
                                    summary={
                                      <div style={{ width: '100px' }}>
                                        <Spinner size='x-small' className='slds-is-relative slds-m-top_medium' />
                                      </div>
                                    }
                                    open={false}
                                  />
                                )
                              }
                              if (error) {
                                return (
                                  <ExpandableOverviewItem
                                    title='Credit report'
                                    summary={<Warning>Failed to load credit report data.</Warning>}
                                    open={false}
                                  />
                                )
                              }
                              return (
                                <CreditReport
                                  creditReport={loanApplication.creditreport}
                                  loanApplication={loanApplication.id}
                                  accounts={data.creditreportaccounts
                                    ? data.creditreportaccounts.edges.map(account => account.node)
                                    : []
                                  }
                                  consolidations={loanApplication.consolidations
                                    ? loanApplication.consolidations.edges.map(consolidation => consolidation.node)
                                    : []
                                  }
                                  open={this.state.creditReportOpen}
                                  selectedFile={this.state.selectedFile}
                                  changeSelectedFile={this.handleSelectedFileChange}
                                  refetchData={this.props.refetchData}
                                  onClick={() => this.setState(prevState => ({ creditReportOpen: !prevState.creditReportOpen }))}
                                  onComplete={() => this.setState({ creditReportOpen: false, securityOpen: true })}
                                />
                              )
                            }}
                          </Query>
                        : <CreditReport
                            creditReport={null}
                            loanApplication={loanApplication.id}
                            accounts={[]}
                            consolidations={[]}
                            open={this.state.creditReportOpen}
                            selectedFile={this.state.selectedFile}
                            changeSelectedFile={this.handleSelectedFileChange}
                            refetchData={this.props.refetchData}
                            onClick={() => this.setState(prevState => ({ creditReportOpen: !prevState.creditReportOpen }))}
                            onComplete={() => this.setState({ creditReportOpen: false, securityOpen: true })}
                          />
                      }
                      {(loanApplication.retailAppliedFor !== null && loanApplication.retailAppliedFor !== 0) &&
                        <Retail loanApplication={loanApplication.id} />
                      }
                      <Calculations
                        loanApplication={loanApplication.id}
                        loans={loans}
                        saveLoan={this.props.updateLoan}
                        calculations={JSON.parse(loanApplication.calculations)}
                        cycle={loanApplication.cycle}
                        employerLoansRules={loanApplication.employerLoansRules}
                        cashAppliedFor={loanApplication.cashAppliedFor}
                        retailAppliedFor={loanApplication.retailAppliedFor}
                        consolidationAppliedFor={loanApplication.consolidationAppliedFor}
                        cashEligibleFor={loanApplication.cashEligibleFor}
                        retailEligibleFor={loanApplication.retailEligibleFor}
                        consolidationEligibleFor={loanApplication.consolidationEligibleFor}
                        overrideAt={loanApplication.overrideAt}
                        overrideBy={loanApplication.overrideBy}
                        updateLoanApplication={this.props.updateLoanApplication}
                        open={this.state.calculationsOpen}
                        refetchData={this.props.refetchData}
                        onClick={() => this.setState(prevState => ({ calculationsOpen: !prevState.calculationsOpen }))}
                        openRequestLoans={() => {
                          this.props.refetchData()
                          this.setState({ requestedLoansOpen: true })
                        }}
                      />
                      <RequestedLoans
                        loans={loans}
                        cashEligibleFor={loanApplication.cashEligibleFor}
                        retailEligibleFor={loanApplication.retailEligibleFor}
                        consolidationEligibleFor={loanApplication.consolidationEligibleFor}
                        open={this.state.requestedLoansOpen}
                        onClick={() => this.setState(prevState => ({ requestedLoansOpen: !prevState.requestedLoansOpen }))}
                        onComplete={() => this.setState({ requestedLoansOpen: false, finaliseOpen: true })}
                      />
                      <Finalise
                        outcome={loanApplication.outcome}
                        outcomeNotes={loanApplication.outcomeNotes}
                        finalisedBy={loanApplication.finalisedBy}
                        finalisedAt={loanApplication.finlisedAt}
                        assignedTo={loanApplication.assignedTo.id}
                        loans={loans}
                        updateLoanApplication={this.props.updateLoanApplication}
                        open={this.state.finaliseOpen}
                        onClick={() => this.setState(prevState => ({ finaliseOpen: !prevState.finaliseOpen }))}
                        onComplete={() => this.setState({ finaliseOpen: false, approveOpen: true })}
                      />
                      <Review
                        loanApplication={loanApplication.id}
                        loanApplicationStatus={loanApplication.status}
                        outcome={loanApplication.outcome}
                        status={loanApplication.status}
                        assignedTo={loanApplication.assignedTo}
                        approvedBy={loanApplication.approvedBy}
                        approvedAt={loanApplication.approvedAt}
                        approverNotes={loanApplication.approverNotes}
                        finalisedBy={loanApplication.finalisedBy}
                        updateLoanApplication={this.props.updateLoanApplication}
                        open={this.state.approveOpen}
                        onClick={() => this.setState(prevState => ({ approveOpen: !prevState.approveOpen }))}
                        onComplete={() => this.setState({ approveOpen: false, issueOpen: true })}
                        onRejected={() => this.setState({ finaliseOpen: true, approveOpen: false })}
                      />
                      <Issue
                        loanApplication={loanApplication}
                        loans={loansWithLmsIds}
                        issuedBy={loanApplication.issuedBy}
                        issuerNotes={loanApplication.issuerNotes}
                        approverNotes={loanApplication.approverNotes}
                        outcomeNotes={loanApplication.outcomeNotes}
                        approvedBy={loanApplication.approvedBy}
                        status={loanApplication.status}
                        updateLoanApplication={this.props.updateLoanApplication}
                        updateLoan={this.props.updateLoan}
                        open={this.state.issueOpen}
                        onClick={() => this.setState(prevState => ({ issueOpen: !prevState.issueOpen }))}
                        collapseSection={() => this.setState({ issueOpen: false })}
                      />
                      <DisburseAndActivate
                        outcome={loanApplication.outcome}
                        issuedBy={loanApplication.issuedBy}
                        status={loanApplication.status}
                        updateLoanApplication={this.props.updateLoanApplication}
                        loansWorkflowRunsProcessing={loansWorkflowRunsProcessing}
                        open={this.state.disburseOpen}
                        onClick={() => this.setState(prevState => ({ disburseOpen: !prevState.disburseOpen }))}
                        onComplete={() => this.setState({ disburseOpen: false })}
                      />
                    </div>
                  </React.Fragment>
                </div>
              </div>
          }
          sizeXLarge={true}
          contentPadding='none'
        />
      )
    } else {
      return (
        <div className='loan-application-modal slds-size_1-of-1'>
          <div className='loan-application-modal-nav slds-border_bottom page-navigation'>
            <div className='u-font-size-larger clickable' onClick={this.props.onClose}>
              <LeftArrow />
              Back
            </div>
          </div>
          <div className='loan-application-modal-file-view slds-text-align_center slds-p-top_large'>
            <div className='u-font-size-larger slds-text-color_inverse'>
              <SvgIcon
                variant='utility'
                symbol='info'
                colour='inverse'
                size='small'
                className='slds-m-right_small'
              />
              No files to display.
            </div>
          </div>
          <div className='slds-scrollable_y loan-application-modal-detail slds-size_1-of-1'>
            <Warning className='slds-m-top_large slds-m-left_large'>
              Failed to retrieve loan application data.
            </Warning>
          </div>
        </div>
      )
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/loanapplication-modal/index.js